import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { MoneyInput } from "@/components/ui/money-input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CalendarIcon } from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { cn } from "@/lib/utils";
import { Cheque } from "@/hooks/useCheques";
import { parseLocalDate, todayLocalDate } from "@/lib/date-utils";
import { calculatePaymentFee, isCardPayment } from "@/lib/paymentFees";

interface ChequeFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: any) => Promise<void>;
  cheque?: Cheque | null;
}

export function ChequeForm({ open, onOpenChange, onSubmit, cheque }: ChequeFormProps) {
  const [formData, setFormData] = useState({
    numero_cheque: "",
    metodo_pagamento: "Pix" as NonNullable<Cheque["metodo_pagamento"]>,
    banco: "",
    agencia: "",
    conta: "",
    emitente: "",
    cpf_cnpj: "",
    valor: 0,
    taxa_operadora_percentual: 0,
    data_emissao: todayLocalDate(),
    data_vencimento: todayLocalDate(),
    data_compensacao: undefined as Date | undefined,
    status: "Pendente" as "Pendente" | "Confirmado" | "Estornado" | "Cancelado",
    observacoes: "",
  });

  useEffect(() => {
    if (cheque) {
      setFormData({
        numero_cheque: cheque.numero_cheque,
        metodo_pagamento: cheque.metodo_pagamento || "Pix",
        banco: cheque.banco,
        agencia: cheque.agencia,
        conta: cheque.conta,
        emitente: cheque.emitente,
        cpf_cnpj: cheque.cpf_cnpj || "",
        valor: cheque.valor,
        taxa_operadora_percentual: Number(cheque.taxa_operadora_percentual || 0),
        data_emissao: parseLocalDate(cheque.data_emissao),
        data_vencimento: parseLocalDate(cheque.data_vencimento),
        data_compensacao: cheque.data_compensacao ? parseLocalDate(cheque.data_compensacao) : undefined,
        status: cheque.status === "Compensado" ? "Confirmado" : cheque.status === "Devolvido" ? "Estornado" : cheque.status === "A Compensar" ? "Pendente" : cheque.status,
        observacoes: cheque.observacoes || "",
      });
    } else {
      setFormData({
        numero_cheque: "",
        metodo_pagamento: "Pix",
        banco: "",
        agencia: "",
        conta: "",
        emitente: "",
        cpf_cnpj: "",
        valor: 0,
        taxa_operadora_percentual: 0,
        data_emissao: todayLocalDate(),
        data_vencimento: todayLocalDate(),
        data_compensacao: undefined,
        status: "Pendente",
        observacoes: "",
      });
    }
  }, [cheque]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const fee = calculatePaymentFee(formData.valor, formData.metodo_pagamento, formData.taxa_operadora_percentual);
    const submitData = {
      ...formData,
      ...fee,
      cpf_cnpj: formData.cpf_cnpj || undefined,
      data_emissao: format(formData.data_emissao, 'yyyy-MM-dd'),
      data_vencimento: format(formData.data_vencimento, 'yyyy-MM-dd'),
      data_compensacao: formData.data_compensacao ? format(formData.data_compensacao, 'yyyy-MM-dd') : undefined,
      observacoes: formData.observacoes || undefined,
    };

    await onSubmit(submitData);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {cheque ? "Editar Pagamento" : "Novo Pagamento"}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="numero_cheque">Referência / ID da transação *</Label>
              <Input
                id="numero_cheque"
                value={formData.numero_cheque}
                onChange={(e) => setFormData({ ...formData, numero_cheque: e.target.value })}
                placeholder="Ex: TXID Pix, NSU, autorização"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="valor">Valor (R$) *</Label>
              <MoneyInput
                id="valor"
                value={formData.valor}
                onValueChange={(valor) => setFormData({ ...formData, valor })}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="metodo_pagamento">Forma de recebimento *</Label>
              <Select value={formData.metodo_pagamento} onValueChange={(value: NonNullable<Cheque["metodo_pagamento"]>) => setFormData({ ...formData, metodo_pagamento: value })} required>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Pix">Pix</SelectItem>
                  <SelectItem value="Transferência">Transferência</SelectItem>
                  <SelectItem value="Maquininha de cartão">Maquininha de cartão</SelectItem>
                  <SelectItem value="Cartão de crédito">Cartão de crédito</SelectItem>
                  <SelectItem value="Cartão de débito">Cartão de débito</SelectItem>
                  <SelectItem value="Dinheiro">Dinheiro</SelectItem>
                  <SelectItem value="Boleto">Boleto</SelectItem>
                  <SelectItem value="Outro">Outro</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="banco">Banco / Operadora *</Label>
              <Input
                id="banco"
                value={formData.banco}
                onChange={(e) => setFormData({ ...formData, banco: e.target.value })}
                placeholder="Ex: Banco, Stone, Cielo, Mercado Pago"
                required
              />
            </div>

            {isCardPayment(formData.metodo_pagamento) && (
              <div className="space-y-2 col-span-2">
                <Label htmlFor="taxa_operadora_percentual">Taxa da operadora (%)</Label>
                <Input
                  id="taxa_operadora_percentual"
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  value={formData.taxa_operadora_percentual}
                  onChange={(event) => setFormData({ ...formData, taxa_operadora_percentual: Number(event.target.value) || 0 })}
                  placeholder="Ex.: 3,49"
                />
                <p className="text-xs text-muted-foreground">
                  Taxa {calculatePaymentFee(formData.valor, formData.metodo_pagamento, formData.taxa_operadora_percentual).valor_taxa_operadora.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })} · Valor líquido {calculatePaymentFee(formData.valor, formData.metodo_pagamento, formData.taxa_operadora_percentual).valor_liquido.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}
                </p>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="agencia">Conta / Chave / Terminal *</Label>
              <Input
                id="agencia"
                value={formData.agencia}
                onChange={(e) => setFormData({ ...formData, agencia: e.target.value })}
                placeholder="Ex: chave Pix, conta, terminal"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="conta">Bandeira / Parcelas / Observação curta *</Label>
              <Input
                id="conta"
                value={formData.conta}
                onChange={(e) => setFormData({ ...formData, conta: e.target.value })}
                placeholder="Ex: Visa 2x, TED, Pix imediato"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="emitente">Pagador / Cliente *</Label>
              <Input
                id="emitente"
                value={formData.emitente}
                onChange={(e) => setFormData({ ...formData, emitente: e.target.value })}
                placeholder="Nome do pagador ou paciente"
                required
              />
            </div>

            <div className="space-y-2 col-span-2">
              <Label htmlFor="cpf_cnpj">CPF/CNPJ</Label>
              <Input
                id="cpf_cnpj"
                value={formData.cpf_cnpj}
                onChange={(e) => setFormData({ ...formData, cpf_cnpj: e.target.value })}
                placeholder="CPF ou CNPJ do pagador"
              />
            </div>

            <div className="space-y-2">
              <Label>Data do Pagamento *</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !formData.data_emissao && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {formData.data_emissao ? format(formData.data_emissao, "PPP", { locale: ptBR }) : <span>Selecione a data</span>}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={formData.data_emissao}
                    onSelect={(date) => date && setFormData({ ...formData, data_emissao: date })}
                    locale={ptBR}
                    initialFocus
                    className="pointer-events-auto"
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div className="space-y-2">
              <Label>Previsão de Crédito *</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !formData.data_vencimento && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {formData.data_vencimento ? format(formData.data_vencimento, "PPP", { locale: ptBR }) : <span>Selecione a data</span>}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={formData.data_vencimento}
                    onSelect={(date) => date && setFormData({ ...formData, data_vencimento: date })}
                    locale={ptBR}
                    initialFocus
                    className="pointer-events-auto"
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div className="space-y-2">
              <Label htmlFor="status">Status *</Label>
              <Select value={formData.status} onValueChange={(value: any) => setFormData({ ...formData, status: value })} required>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Pendente">Pendente</SelectItem>
                  <SelectItem value="Confirmado">Confirmado</SelectItem>
                  <SelectItem value="Estornado">Estornado</SelectItem>
                  <SelectItem value="Cancelado">Cancelado</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {(formData.status === "Confirmado" || formData.data_compensacao) && (
              <div className="space-y-2">
                <Label>Data de Confirmação</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal",
                        !formData.data_compensacao && "text-muted-foreground"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {formData.data_compensacao ? format(formData.data_compensacao, "PPP", { locale: ptBR }) : <span>Selecione a data</span>}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={formData.data_compensacao}
                      onSelect={(date) => setFormData({ ...formData, data_compensacao: date })}
                      locale={ptBR}
                      initialFocus
                      className="pointer-events-auto"
                    />
                  </PopoverContent>
                </Popover>
              </div>
            )}

            <div className="space-y-2 col-span-2">
              <Label htmlFor="observacoes">Observações</Label>
              <Textarea
                id="observacoes"
                value={formData.observacoes}
                onChange={(e) => setFormData({ ...formData, observacoes: e.target.value })}
                rows={3}
              />
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit">
              {cheque ? "Atualizar" : "Cadastrar"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
